import axios, { AxiosError, AxiosInstance } from "axios";
import secureLocalStorage from "react-secure-storage";
import { API_URL } from "../../utils/globals";
import {
  attachDisplayMessage,
  normalizeApiError,
  notifyApiError,
} from "./apiError";

type CreateApiClientOptions = {
  notifyErrors?: boolean;
  handleUnauthorized?: boolean;
  handleSessionExpiry?: boolean;
};

let unauthorizedRedirectStarted = false;

const redirectToLogin = () => {
  if (unauthorizedRedirectStarted) return;
  unauthorizedRedirectStarted = true;
  secureLocalStorage.removeItem("jwt");
  sessionStorage.clear();
  if (window.location.pathname !== "/login") {
    window.location.replace("/login");
  }
};

export const createApiClient = (
  options: CreateApiClientOptions = {},
): AxiosInstance => {
  const { notifyErrors = true, handleUnauthorized = true } = options;

  const api = axios.create({
    baseURL: API_URL,
    headers: {
      "Content-Type": "application/json",
    },
  });

  api.interceptors.request.use(
    (config) => {
      if (!handleUnauthorized) {
        return config;
      }
      const token = secureLocalStorage.getItem("jwt");
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error),
  );

  api.interceptors.response.use(
    (response) => response,
    (error: AxiosError) => {
      const normalized = normalizeApiError(error);
      attachDisplayMessage(error, normalized);

      if (
        handleUnauthorized &&
        error.response &&
        (normalized.status === 401 || normalized.status === 403)
      ) {
        redirectToLogin();
        return Promise.reject(error);
      }

      if (notifyErrors) {
        notifyApiError(normalized.displayMessage);
      }

      return Promise.reject(error);
    },
  );

  return api;
};
